import { useState, useEffect } from 'react';
import { aboutPageService } from '@/api/services';
import { iconMap, resolveSiteBuilderIconComponent } from '@/utils/siteBuilderIconMap';

function mapAboutSections(sectionsData) {
  return (Array.isArray(sectionsData) ? sectionsData : []).map((sec) => ({
    ...sec,
    items: sec.items?.map((item) => ({
      ...item,
      IconComponent: resolveSiteBuilderIconComponent(item.icon)
    })) || []
  }));
}

export { iconMap };

/**
 * Hook to fetch and manage about page data
 * @param {string} sectionKey - Optional section key to fetch a specific section
 * @returns {object} { sections, section, loading, error }
 */
export const useAboutPage = (sectionKey = null, initialSections = null) => {
  const [sections, setSections] = useState(() => (initialSections != null && !sectionKey ? mapAboutSections(initialSections) : []));
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(!(initialSections != null && !sectionKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialSections != null && !sectionKey) {
      setSections(mapAboutSections(initialSections));
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (sectionKey) {
          // Fetch specific section by key
          const response = await aboutPageService.getSectionByKey(sectionKey);
          if (response.data?.success) {
            const sectionData = response.data.data;
            // Map icon names to components
            if (sectionData.items) {
              sectionData.items = sectionData.items.map(item => ({
                ...item,
                IconComponent: resolveSiteBuilderIconComponent(item.icon)
              }));
            }
            setSection(sectionData);
          }
        } else {
          // Fetch all sections
          const response = await aboutPageService.getAllSections();
          if (response.data?.success) {
            setSections(mapAboutSections(response.data.data || []));
          }
        }
      } catch (err) {
        console.error('Error fetching about page data:', err);
        setError(err.message || 'Failed to load about page data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sectionKey, initialSections]);

  return { sections, section, loading, error };
};

/**
 * Get a specific section from the sections array by key
 * @param {array} sections - Array of sections
 * @param {string} key - Section key
 * @returns {object|null} Section object or null
 */
export const getAboutSectionByKey = (sections, key) => {
  return sections.find(sec => sec.section_key === key) || null;
};

export default useAboutPage;

